import { createContext, useContext, type ReactNode } from 'react';

export type ComposeMessageHandler = (text: string) => void;

const ComposeMessageContext = createContext<ComposeMessageHandler | null>(null);

/**
 * Lets a content block (e.g. a row selected in a results table) fill the
 * composer with a standard inquiry message, without any screen-specific
 * wiring — a block that wants this just calls `useComposeMessage()` and
 * invokes it with whatever text it wants (typically an i18n string with
 * interpolated row data, e.g. `t('...OrderDetailsRequest', row)`). Screens
 * that don't call it are unaffected; the composer is untouched until
 * something explicitly asks to fill it, and doing so always replaces
 * whatever is currently in the field.
 */
export function ComposeMessageProvider({
  onCompose,
  children,
}: {
  onCompose: ComposeMessageHandler;
  children: ReactNode;
}) {
  return <ComposeMessageContext.Provider value={onCompose}>{children}</ComposeMessageContext.Provider>;
}

export function useComposeMessage(): ComposeMessageHandler | null {
  return useContext(ComposeMessageContext);
}
